import { useState, useCallback, useMemo } from "react";
import { Image as MantineImage } from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { polymorphicComponentWrapper } from "../../utilities";
import type {
    CustomImageFactory,
    IProps,
    PreviewImage,
    ImagePreviewContextValue,
    ImagePreviewHandlersValue,
} from "./image.types";
import styles from "./image.module.scss";
import { Modal } from "../modal";
import { useImagePreview } from "../../hooks/use-image-preview";
import { ImagePreviewStateContext, ImagePreviewHandlersContext } from "./image-preview-context";
import { ImagePreviewViewer } from "./components/viewer";
import { ImagePreviewToolbox } from "./components/toolbox";
import { ImagePreviewImage } from "./components/image-preview-image/image-preview-image";
import { Group } from "../group";

/**
 * Standard Image component wrapping Mantine's Image.
 * Supports polymorphic 'component' prop.
 *
 * @returns {JSX.Element} The Image component.
 */
const Image = polymorphicComponentWrapper<CustomImageFactory, IProps>("Image", (props, ref) => {
    return <MantineImage ref={ref} classNames={{ root: styles.root }} {...props} />;
});

/**
 * ImagePreview component that provides a context for a gallery of images.
 * It renders its children and a full-screen modal preview.
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Components that can contain ImagePreview.Image triggers.
 * @returns {JSX.Element} The ImagePreview provider and modal.
 */
const ImagePreview = ({ children }: { children: React.ReactNode }) => {
    const { state, handlers: baseHandlers } = useImagePreview();
    const [images, setImages] = useState<PreviewImage[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);

    const registerImage = useCallback((img: PreviewImage) => {
        setImages((prev) => {
            if (prev.some((i) => i.src === img.src)) return prev;
            return [...prev, img];
        });
        return () => {
            setImages((prev) => prev.filter((i) => i.src !== img.src));
        };
    }, []);

    const setIndex = useCallback(
        (index: number) => {
            setCurrentIndex(index);
            baseHandlers.reset();
        },
        [baseHandlers]
    );

    const next = useCallback(() => {
        setImages((currentImages) => {
            if (currentImages.length === 0) return currentImages;
            setCurrentIndex((prevIndex) => (prevIndex + 1) % currentImages.length);
            return currentImages;
        });
    }, []);

    const previous = useCallback(() => {
        setImages((currentImages) => {
            if (currentImages.length === 0) return currentImages;
            setCurrentIndex((prevIndex) => (prevIndex - 1 + currentImages.length) % currentImages.length);
            return currentImages;
        });
    }, []);

    useHotkeys([
        ["ArrowLeft", previous],
        ["ArrowRight", next],
    ]);

    const stateValue = useMemo<ImagePreviewContextValue>(
        () => ({
            state,
            images,
            currentIndex,
        }),
        [state, images, currentIndex]
    );

    const handlersValue = useMemo<ImagePreviewHandlersValue>(
        () => ({
            ...baseHandlers,
            registerImage,
            setIndex,
            next,
            previous,
        }),
        [baseHandlers, registerImage, setIndex, next, previous]
    );

    return (
        <ImagePreviewHandlersContext.Provider value={handlersValue}>
            <ImagePreviewStateContext.Provider value={stateValue}>
                {children}
                <Modal.Root
                    zIndex={1000}
                    size="100%"
                    opened={state.opened}
                    trapFocus
                    closeOnEscape={false}
                    onClose={baseHandlers.close}
                    padding={0}
                    centered
                    classNames={{
                        overlay: styles.previewOverlay,
                        root: styles.previewRoot,
                        inner: styles.previewInner,
                        content: styles.previewContent,
                    }}
                >
                    <Modal.Overlay blur={10} />
                    <Modal.Content data-mantine-stop-propagation="true">
                        <ImagePreviewViewer />

                        <ImagePreviewToolbox>
                            <Group gap={images.length > 1 ? "xl" : "lg"}>
                                <Group gap="xs">
                                    <ImagePreviewToolbox.ZoomIn />
                                    <ImagePreviewToolbox.ZoomOut />
                                    <ImagePreviewToolbox.Reset />
                                    <ImagePreviewToolbox.Rotate />
                                </Group>
                                <Group gap="xs">
                                    <Group gap={0}>
                                        <ImagePreviewToolbox.Previous />
                                        <ImagePreviewToolbox.Counts />
                                        <ImagePreviewToolbox.Next />
                                    </Group>
                                    <ImagePreviewToolbox.Close />
                                </Group>
                            </Group>
                        </ImagePreviewToolbox>
                    </Modal.Content>
                </Modal.Root>
            </ImagePreviewStateContext.Provider>
        </ImagePreviewHandlersContext.Provider>
    );
};

// Composable components assignment
ImagePreview.Viewer = ImagePreviewViewer;
ImagePreview.Toolbox = ImagePreviewToolbox;
ImagePreview.Image = ImagePreviewImage;

Image.Preview = ImagePreview;

export { Image, ImagePreview };
